'use client';
import { STATUS, SURFACE, BORDER } from '@/constants/colors';
import { memo, useState, useEffect, useContext } from 'react';
import {
  Box,
  Drawer,
  Stack,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Divider,
  useMediaQuery,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Home,
  History,
  LocalShipping,
  AccountCircle,
  Settings,
  HelpOutline,
  Logout,
  Menu,
  ChevronLeft,
  Calculate,
  Dashboard,
  LocationOn,
  BarChart,
} from '@mui/icons-material';
import { useRouter, usePathname } from 'next/navigation';
import { ReactReduxContext } from 'react-redux';
import { themeColors } from '../theme/theme';
import { TopNav } from './TopNav';
import { BottomNav } from './BottomNav';
import { useTranslation } from '@/kit/useTranslation';
import { LanguageSwitch } from '@/kit/LanguageSwitch';
import { logout as logoutAuth } from '../app/redux/slices/authSlice';
import { resetUserProfile } from '../app/redux/slices/userProfileSlice';
import { clearAddressBook } from '../app/redux/slices/addressBookSlice';
import { logout as logoutApi } from '../app/redux/slices/apiSlice';
import { logout as logoutBooking } from '../app/redux/slices/bookingSlice';
import { persistor } from '../app/redux/store';

const DESKTOP_SIDEBAR_WIDTH = 260;
const DESKTOP_SIDEBAR_COLLAPSED = 72;

/**
 * ResponsiveLayout - Intelligent layout that adapts to screen size
 *
 * Mobile: Bottom navigation + Side drawer
 * Desktop: Persistent sidebar + Optional side panel
 */
const ResponsiveLayout = memo(
  ({
    children,
    sidePanel = null, // Optional component for right side (e.g., PriceEstimator)
    sidePanelWidth = 380,
    showSidePanel = true,
  }) => {
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
    const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));
    const router = useRouter();
    const pathname = usePathname();

    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

    // Get Redux state
    const reduxContext = useContext(ReactReduxContext);
    let loggedIn = false;
    let user = null;
    let dispatch = () => {};

    if (reduxContext?.store) {
      const state = reduxContext.store.getState();
      loggedIn = state?.auth?.isLoggedIn;
      user = state?.userProfile?.data?.user;
      dispatch = reduxContext.store.dispatch;
    }

    const isBusiness = user?.userType?.toLowerCase() === 'business';
    const { t } = useTranslation();

    // Navigation items
    const mainNavItems = isBusiness
      ? [
          {
            key: 'business-dashboard',
            label: t('nav.dashboard'),
            icon: Dashboard,
            path: '/business-dashboard',
          },
          {
            key: 'activeParcels',
            label: t('nav.activeParcels'),
            icon: LocalShipping,
            path: '/activeParcels',
          },
          { key: 'history', label: t('nav.history'), icon: History, path: '/history' },
          {
            key: 'saved-addresses',
            label: t('nav.addresses'),
            icon: LocationOn,
            path: '/saved-addresses',
          },
          {
            key: 'analytics',
            label: t('nav.analytics'),
            icon: BarChart,
            path: '/business-analytics',
          },
        ]
      : [
          { key: 'home', label: t('nav.home'), icon: Home, path: '/home' },
          {
            key: 'activeParcels',
            label: t('nav.activeParcels'),
            icon: LocalShipping,
            path: '/activeParcels',
          },
          { key: 'history', label: t('nav.history'), icon: History, path: '/history' },
        ];

    const bottomNavItems = [
      {
        key: 'profile',
        label: t('nav.profile'),
        icon: AccountCircle,
        path: '/profile',
      },
      { key: 'support', label: t('nav.support'), icon: HelpOutline, path: '/support' },
    ];

    const getActiveKey = () => {
      if (!pathname) return 'home';

      // Remove leading slash and get the first segment
      const cleanPath = pathname.replace(/^\//, '');
      const firstSegment = cleanPath.split('/')[0] || 'home';

      // Map paths to keys
      const pathToKeyMap = {
        'business-dashboard': 'business-dashboard',
        'business-analytics': 'analytics',
        'saved-addresses': 'saved-addresses',
        activeParcels: 'activeParcels',
        history: 'history',
        home: 'home',
        profile: 'profile',
        support: 'support',
        wallet: 'wallet',
        invoices: 'invoices',
        estimate: 'estimate',
        booking: 'booking',
      };

      return pathToKeyMap[firstSegment] || firstSegment;
    };

    const handleNav = (path) => {
      router.push(path);
      setMobileDrawerOpen(false);
    };

    const handleLogout = async () => {
      try {
        dispatch(logoutAuth());
        dispatch(logoutApi());
        dispatch(logoutBooking());
        dispatch(resetUserProfile());
        dispatch(clearAddressBook());
        await persistor.purge();
        ['auth', 'api', 'addressBook', 'booking', 'userProfile', 'persist:root'].forEach((key) => {
          localStorage.removeItem(key);
        });
        router.replace('/login');
      } catch (error) {
        console.error('Logout error:', error);
        router.replace('/login');
      }
    };

    // Desktop Sidebar
    const DesktopSidebar = () => (
      <Box
        sx={{
          width: sidebarOpen ? DESKTOP_SIDEBAR_WIDTH : DESKTOP_SIDEBAR_COLLAPSED,
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bgcolor: '#fff',
          borderRight: '1px solid #e5e7eb',
          transition: 'width 0.3s ease',
          zIndex: 1200,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Logo/Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent={sidebarOpen ? 'space-between' : 'center'}
          sx={{ p: 2, borderBottom: '1px solid #f3f4f6' }}
        >
          {sidebarOpen && (
            <Typography
              sx={{
                fontSize: '1.25rem',
                fontWeight: 800,
                color: themeColors.primaryColor,
                letterSpacing: '-0.02em',
              }}
            >
              ProjectL
            </Typography>
          )}
          <IconButton onClick={() => setSidebarOpen(!sidebarOpen)} sx={{ color: '#6b7280' }}>
            {sidebarOpen ? <ChevronLeft /> : <Menu />}
          </IconButton>
        </Stack>

        {/* User Info */}
        {loggedIn && user && sidebarOpen && (
          <Box sx={{ p: 2, borderBottom: '1px solid #f3f4f6' }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar
                src={user.avatar}
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: themeColors.primaryColor,
                }}
              >
                {user.userName?.[0] || 'U'}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: '0.9rem',
                    fontWeight: 700,
                    color: '#1f2937',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {user.userName || t('common.user')}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    color: '#6b7280',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {isBusiness ? t('profile.businessAccount') : t('nav.individual')}
                </Typography>
              </Box>
            </Stack>
          </Box>
        )}

        {/* Main Nav */}
        <Box sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
          <List disablePadding>
            {mainNavItems.map((item) => {
              const isActive = getActiveKey() === item.key;
              return (
                <ListItem key={item.key} disablePadding sx={{ px: 1 }}>
                  <ListItemButton
                    onClick={() => handleNav(item.path)}
                    sx={{
                      borderRadius: 2,
                      mb: 0.5,
                      py: 1.25,
                      px: sidebarOpen ? 2 : 1.5,
                      justifyContent: sidebarOpen ? 'flex-start' : 'center',
                      bgcolor: isActive ? alpha(themeColors.primaryColor, 0.1) : 'transparent',
                      '&:hover': {
                        bgcolor: isActive ? alpha(themeColors.primaryColor, 0.15) : '#f9fafb',
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: sidebarOpen ? 40 : 'auto',
                        color: isActive ? themeColors.primaryColor : '#6b7280',
                      }}
                    >
                      <item.icon sx={{ fontSize: 22 }} />
                    </ListItemIcon>
                    {sidebarOpen && (
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{
                          fontSize: '0.875rem',
                          fontWeight: isActive ? 700 : 500,
                          color: isActive ? themeColors.primaryColor : '#374151',
                        }}
                      />
                    )}
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>

          <Divider sx={{ my: 1.5 }} />

          <List disablePadding>
            {bottomNavItems.map((item) => {
              const isActive = getActiveKey() === item.key;
              return (
                <ListItem key={item.key} disablePadding sx={{ px: 1 }}>
                  <ListItemButton
                    onClick={() => handleNav(item.path)}
                    sx={{
                      borderRadius: 2,
                      mb: 0.5,
                      py: 1,
                      px: sidebarOpen ? 2 : 1.5,
                      justifyContent: sidebarOpen ? 'flex-start' : 'center',
                      bgcolor: isActive ? alpha(themeColors.primaryColor, 0.1) : 'transparent',
                      '&:hover': {
                        bgcolor: isActive ? alpha(themeColors.primaryColor, 0.15) : '#f9fafb',
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: sidebarOpen ? 40 : 'auto',
                        color: isActive ? themeColors.primaryColor : '#6b7280',
                      }}
                    >
                      <item.icon sx={{ fontSize: 20 }} />
                    </ListItemIcon>
                    {sidebarOpen && (
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{
                          fontSize: '0.85rem',
                          fontWeight: isActive ? 600 : 500,
                          color: isActive ? themeColors.primaryColor : '#4b5563',
                        }}
                      />
                    )}
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Box>

        {/* Language */}
        {sidebarOpen && (
          <Box sx={{ px: 2, pb: 1, borderTop: '1px solid #f3f4f6', pt: 1.5 }}>
            <LanguageSwitch variant="sidebar" />
          </Box>
        )}

        {/* Logout */}
        <Box sx={{ p: 1, borderTop: sidebarOpen ? 'none' : '1px solid #f3f4f6' }}>
          <ListItemButton
            onClick={handleLogout}
            sx={{
              borderRadius: 2,
              py: 1.25,
              px: sidebarOpen ? 2 : 1.5,
              justifyContent: sidebarOpen ? 'flex-start' : 'center',
              color: '#dc2626',
              '&:hover': {
                bgcolor: alpha('#dc2626', 0.08),
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: sidebarOpen ? 40 : 'auto', color: '#dc2626' }}>
              <Logout sx={{ fontSize: 20 }} />
            </ListItemIcon>
            {sidebarOpen && (
              <ListItemText
                primary={t('nav.signOut')}
                primaryTypographyProps={{
                  fontSize: '0.85rem',
                  fontWeight: 600,
                }}
              />
            )}
          </ListItemButton>
        </Box>
      </Box>
    );

    // Mobile view (existing behavior)
    if (!isDesktop && !isTablet) {
      return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#fafafa' }}>
          <TopNav
            user={user}
            onMenuClick={() => setMobileDrawerOpen(true)}
            onAvatarClick={() => router.push('/profile')}
            onNotificationsClick={() => router.push('/notifications')}
            onWalletClick={() => router.push('/wallet')}
          />
          <Box sx={{ pb: 10 }}>{children}</Box>
          <BottomNav
            active={getActiveKey()}
            onNav={(key) => {
              const item = [...mainNavItems, ...bottomNavItems].find((i) => i.key === key);
              if (item) handleNav(item.path);
              else if (key === 'home') handleNav('/home');
            }}
            isLoggedIn={loggedIn}
          />
        </Box>
      );
    }

    // Desktop/Tablet view
    const mainContentMargin = sidebarOpen ? DESKTOP_SIDEBAR_WIDTH : DESKTOP_SIDEBAR_COLLAPSED;
    const showRightPanel = isDesktop && showSidePanel && sidePanel;

    return (
      <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f9fafb' }}>
        {/* Desktop Sidebar */}
        <DesktopSidebar />

        {/* Main Content */}
        <Box
          sx={{
            flex: 1,
            ml: `${mainContentMargin}px`,
            mr: showRightPanel ? `${sidePanelWidth}px` : 0,
            transition: 'margin 0.3s ease',
            minHeight: '100vh',
          }}
        >
          {children}
        </Box>

        {/* Right Side Panel (Desktop only) */}
        {showRightPanel && (
          <Box
            sx={{
              width: sidePanelWidth,
              height: '100vh',
              position: 'fixed',
              right: 0,
              top: 0,
              bgcolor: '#fff',
              borderLeft: '1px solid #e5e7eb',
              overflowY: 'auto',
              zIndex: 1100,
            }}
          >
            {sidePanel}
          </Box>
        )}
      </Box>
    );
  }
);

ResponsiveLayout.displayName = 'ResponsiveLayout';

export { ResponsiveLayout };
export default ResponsiveLayout;
